const normalizeRodText = (value) =>
  typeof value === "string" ? value.trim() : "";

const capitalizeTechnique = (value) =>
  normalizeRodText(value)
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const formatRodLength = (lengthFt) => {
  const parsed = Number(lengthFt);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";

  const totalInches = Math.round(parsed * 12);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;

  if (inches === 0) {
    return `${feet}ft`;
  }

  return `${feet}'${inches}"`;
};

const formatRodCastWeight = (minWeight, maxWeight) => {
  const min = Number(minWeight);
  const max = Number(maxWeight);
  const hasMin = Number.isFinite(min) && min > 0;
  const hasMax = Number.isFinite(max) && max > 0;

  if (hasMin && hasMax) {
    return `${min}\u2013${max}g`;
  }

  if (hasMin) {
    return `${min}g`;
  }

  if (hasMax) {
    return `${max}g`;
  }

  return "";
};

const buildRodDisplayName = (rodItem) => {
  const brand = normalizeRodText(rodItem?.brand);
  const family = normalizeRodText(rodItem?.family);
  const model = normalizeRodText(rodItem?.model);
  const variant = normalizeRodText(rodItem?.variant);

  return [brand, family, model, variant].filter(Boolean).join(" ").trim();
};

const buildRodDisplaySubtitle = (rodItem) => {
  const length = formatRodLength(rodItem?.length_ft);
  const castWeight = formatRodCastWeight(rodItem?.cast_min_g, rodItem?.cast_max_g);
  const technique = capitalizeTechnique(rodItem?.technique);

  return [length, castWeight, technique].filter(Boolean).join(" | ");
};

const buildRodSearchText = (rodItem, displayName, displaySubtitle) =>
  [
    rodItem?.brand,
    rodItem?.family,
    rodItem?.model,
    rodItem?.variant,
    rodItem?.technique,
    rodItem?.length_ft,
    rodItem?.cast_min_g,
    rodItem?.cast_max_g,
    displayName,
    displaySubtitle,
  ]
    .map((value) => normalizeRodText(String(value ?? "")))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const getRodSuggestions = (database, input) => {
  if (!input || !Array.isArray(database)) return [];
  const lower = input.toLowerCase();

  return database
    .map((rodItem) => {
      const displayName = buildRodDisplayName(rodItem);
      const displaySubtitle = buildRodDisplaySubtitle(rodItem);
      const value = displayName || normalizeRodText(rodItem?.brand);
      const searchText = buildRodSearchText(rodItem, displayName, displaySubtitle);

      if (!value) {
        return null;
      }

      return {
        value,
        displayName: value,
        displaySubtitle,
        searchText,
      };
    })
    .filter((suggestion) => suggestion && suggestion.searchText.includes(lower))
    .slice(0, 8);
};

const normalizeReelText = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeReelCompare = (value) =>
  normalizeReelText(value)
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const buildReelDisplayName = (reelItem) => {
  const brand = normalizeReelText(reelItem?.brand);
  const family = normalizeReelText(reelItem?.family);
  const model = normalizeReelText(reelItem?.model);
  const variant = normalizeReelText(reelItem?.variant);
  const size = normalizeReelText(
    reelItem?.size == null ? "" : String(reelItem.size)
  );
  const swBadge =
    normalizeReelCompare(reelItem?.water_type) === "saltwater" ? "SW" : "";

  return [brand, family, model, variant, size, swBadge]
    .filter(Boolean)
    .join(" ")
    .trim();
};

const buildReelSearchText = (reelItem, displayName) =>
  [
    reelItem?.brand,
    reelItem?.family,
    reelItem?.model,
    reelItem?.variant,
    reelItem?.category,
    reelItem?.water_type,
    reelItem?.size,
    displayName,
  ]
    .map((value) => normalizeReelText(String(value ?? "")))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const getReelSuggestions = (database, input) => {
  if (!input || !Array.isArray(database)) return [];
  const lower = input.toLowerCase();
  const seen = new Set();

  return database
    .map((reelItem) => {
      const value = buildReelDisplayName(reelItem);
      if (!value) {
        return null;
      }

      return {
        value,
        searchText: buildReelSearchText(reelItem, value),
      };
    })
    .filter((suggestion) => {
      if (!suggestion || !suggestion.searchText.includes(lower)) {
        return false;
      }

      const key = suggestion.value.toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .slice(0, 8);
};

const normalizeGearText = (value) =>
  typeof value === "string" ? value.trim() : "";

export const getGearModelSuggestions = (database, input) => {
  if (!input || !Array.isArray(database)) return [];
  const lower = input.toLowerCase();

  return database
    .flatMap((brandEntry) => {
      const brand = normalizeGearText(brandEntry?.brand);
      const models = Array.isArray(brandEntry?.models) ? brandEntry.models : [];

      return models.flatMap((modelEntry) => {
        const model =
          typeof modelEntry === "string"
            ? normalizeGearText(modelEntry)
            : normalizeGearText(modelEntry?.model);
        const value = [brand, model].filter(Boolean).join(" ").trim();

        if (!value || !value.toLowerCase().includes(lower)) {
          return [];
        }

        return [value];
      });
    })
    .slice(0, 8);
};

const formatLineTypeLabel = (value) => {
  const normalized = normalizeGearText(value).toLowerCase();
  if (!normalized) return "";
  if (normalized === "braid") return "Braid";
  if (normalized === "monofilament") return "Mono";
  if (normalized === "fluorocarbon") return "Fluorocarbon";
  if (normalized === "fused") return "Fused";

  return normalized
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatLineXCount = (value) => {
  const normalized = normalizeGearText(
    value == null ? "" : String(value)
  ).toLowerCase();
  if (!normalized) return "";
  if (normalized.startsWith("x")) return normalized;
  return `x${normalized}`;
};

const formatLineMeasure = (value, suffix) => {
  if (value == null || value === "") return "";

  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value} ${suffix}`;
  }

  const text = normalizeGearText(String(value));
  if (!text) return "";
  if (text.toLowerCase().includes(suffix.toLowerCase())) {
    return text;
  }

  return `${text} ${suffix}`;
};

const buildLineLabel = (brand, model) =>
  [normalizeGearText(brand), normalizeGearText(model)]
    .filter(Boolean)
    .join(" ")
    .trim();

const buildLineSubtitle = (modelEntry, sizeEntry) => {
  const parts = [
    formatLineXCount(modelEntry?.x),
    formatLineMeasure(sizeEntry?.mm, "mm"),
    formatLineMeasure(sizeEntry?.lb, "lb"),
    formatLineTypeLabel(modelEntry?.type),
  ].filter(Boolean);

  return parts.join(" | ");
};

const buildLineSearchText = (brand, modelEntry, label, subtitle, sizeEntry) =>
  [
    normalizeGearText(brand),
    normalizeGearText(modelEntry?.model),
    normalizeGearText(modelEntry?.type),
    formatLineTypeLabel(modelEntry?.type),
    formatLineXCount(modelEntry?.x),
    formatLineMeasure(sizeEntry?.mm, "mm"),
    formatLineMeasure(sizeEntry?.lb, "lb"),
    label,
    subtitle,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const getLineSuggestions = (database, input) => {
  if (!input || !Array.isArray(database)) return [];
  const lower = input.toLowerCase();
  const seen = new Set();

  return database
    .flatMap((brandEntry) => {
      const brand = normalizeGearText(brandEntry?.brand);
      const models = Array.isArray(brandEntry?.models) ? brandEntry.models : [];

      return models.flatMap((modelEntry) => {
        const model = normalizeGearText(modelEntry?.model);
        const label = buildLineLabel(brand, model);
        if (!label) {
          return [];
        }

        const sizeRows =
          Array.isArray(modelEntry?.sizes) && modelEntry.sizes.length > 0
            ? modelEntry.sizes
            : [null];

        return sizeRows.map((sizeEntry, index) => {
          const subtitle = buildLineSubtitle(modelEntry, sizeEntry);
          const value = subtitle ? `${label} - ${subtitle}` : label;
          const searchText = buildLineSearchText(
            brand,
            modelEntry,
            label,
            subtitle,
            sizeEntry
          );

          return {
            key: `${brand}-${model}-${subtitle || "base"}-${index}`,
            value,
            displayName: label,
            displaySubtitle: subtitle,
            searchText,
          };
        });
      });
    })
    .filter((suggestion) => {
      if (!suggestion || !suggestion.searchText.includes(lower)) {
        return false;
      }

      const dedupeKey = suggestion.value.toLowerCase();
      if (seen.has(dedupeKey)) {
        return false;
      }

      seen.add(dedupeKey);
      return true;
    })
    .slice(0, 8);
};

const buildLureComposedLabel = (lureItem) =>
  [
    normalizeGearText(lureItem?.brand),
    normalizeGearText(lureItem?.model),
    normalizeGearText(lureItem?.submodel),
    normalizeGearText(lureItem?.buoyancy),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

const buildLureDisplayLabel = (lureItem) => {
  const displayName = normalizeGearText(lureItem?.display_name);
  const composedLabel = buildLureComposedLabel(lureItem);
  const idFallback = normalizeGearText(lureItem?.id);

  return displayName || composedLabel || idFallback;
};

const buildLureDisplaySubtitle = (lureItem, displayLabel) => {
  const displayName = normalizeGearText(lureItem?.display_name);
  const composedLabel = buildLureComposedLabel(lureItem);

  if (
    displayName &&
    composedLabel &&
    composedLabel.toLowerCase() !== displayLabel.toLowerCase()
  ) {
    return composedLabel;
  }

  return "";
};

const buildLureSearchText = (lureItem, displayLabel, displaySubtitle) =>
  [
    normalizeGearText(lureItem?.id),
    normalizeGearText(lureItem?.brand),
    normalizeGearText(lureItem?.model),
    normalizeGearText(lureItem?.submodel),
    normalizeGearText(lureItem?.buoyancy),
    normalizeGearText(lureItem?.display_name),
    displayLabel,
    displaySubtitle,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const getLureSuggestions = (database, input) => {
  if (!input || !Array.isArray(database)) return [];
  const lower = input.toLowerCase();
  const seen = new Set();

  return database
    .map((lureItem) => {
      const displayLabel = buildLureDisplayLabel(lureItem);
      if (!displayLabel) {
        return null;
      }

      const displaySubtitle = buildLureDisplaySubtitle(lureItem, displayLabel);
      const searchText = buildLureSearchText(
        lureItem,
        displayLabel,
        displaySubtitle
      );

      return {
        key: normalizeGearText(lureItem?.id) || displayLabel,
        value: displayLabel,
        displayName: displayLabel,
        displaySubtitle,
        searchText,
      };
    })
    .filter((suggestion) => {
      if (!suggestion || !suggestion.searchText.includes(lower)) {
        return false;
      }

      const dedupeKey = suggestion.value.toLowerCase();
      if (seen.has(dedupeKey)) {
        return false;
      }

      seen.add(dedupeKey);
      return true;
    })
    .slice(0, 8);
};

export const getBaitSuggestions = (
  input,
  { baitDatabase, liveBaitDatabase, deadBaitDatabase } = {}
) => {
  if (!input) return [];
  const lower = input.toLowerCase();
  const databases = [baitDatabase, liveBaitDatabase, deadBaitDatabase].filter(
    Array.isArray
  );
  const seen = new Set();

  return databases
    .flatMap((database) => {
      if (database.length === 0) return [];

      if (typeof database[0] === "string") {
        return database.filter((item) => item.toLowerCase().includes(lower));
      }

      return database
        .filter((item) => {
          const nameMatch = item.name?.toLowerCase().includes(lower);
          const baseMatch = item.base?.toLowerCase().includes(lower);
          const categoryMatch = item.category?.toLowerCase().includes(lower);
          return nameMatch || baseMatch || categoryMatch;
        })
        .map((item) => item.name);
    })
    .filter((value) => {
      const normalized = normalizeGearText(value);
      if (!normalized) return false;

      const dedupeKey = normalized.toLowerCase();
      if (seen.has(dedupeKey)) return false;

      seen.add(dedupeKey);
      return true;
    })
    .slice(0, 8);
};

export const getLureOrBaitSuggestions = (
  { lureDatabase, baitDatabase, liveBaitDatabase, deadBaitDatabase } = {},
  input
) => {
  const seen = new Set();
  return [
    ...getLureSuggestions(lureDatabase, input),
    ...getBaitSuggestions(input, {
      baitDatabase,
      liveBaitDatabase,
      deadBaitDatabase,
    }).map((value) => ({ value, displayName: value, displaySubtitle: "" })),
  ]
    .filter((suggestion) => {
      const dedupeKey = normalizeGearText(suggestion?.value).toLowerCase();
      if (!dedupeKey || seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    })
    .slice(0, 8);
};

const formatHookTypeLabel = (value) => {
  const normalized = normalizeGearText(value).toLowerCase();
  if (!normalized) return "";
  if (normalized === "single" || normalized === "single_hook") {
    return "Single Hook";
  }
  if (normalized === "treble" || normalized === "treble_hook") {
    return "Treble Hook";
  }

  return normalized
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const getHookSuggestions = (database, input) => {
  if (!input || !Array.isArray(database)) return [];
  const lower = input.toLowerCase();
  const seen = new Set();

  return database
    .flatMap((brandEntry) => {
      const brand = normalizeGearText(brandEntry?.brand);
      const models = Array.isArray(brandEntry?.models) ? brandEntry.models : [];

      return models.flatMap((modelEntry, index) => {
        const model =
          typeof modelEntry === "string"
            ? normalizeGearText(modelEntry)
            : normalizeGearText(modelEntry?.model);
        const type =
          typeof modelEntry === "string" ? "" : formatHookTypeLabel(modelEntry?.type);
        const displayName = [brand, model].filter(Boolean).join(" ").trim();

        if (!displayName) {
          return [];
        }

        const searchText = [
          brand,
          model,
          typeof modelEntry === "string" ? "" : normalizeGearText(modelEntry?.type),
          type,
          displayName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return [
          {
            key: `${displayName}-${type || "base"}-${index}`,
            value: displayName,
            displayName,
            displaySubtitle: type,
            searchText,
          },
        ];
      });
    })
    .filter((suggestion) => {
      if (!suggestion.searchText.includes(lower)) {
        return false;
      }

      const dedupeKey = `${suggestion.value}::${suggestion.displaySubtitle}`.toLowerCase();
      if (seen.has(dedupeKey)) {
        return false;
      }

      seen.add(dedupeKey);
      return true;
    })
    .slice(0, 8);
};
